import { Effect } from "../inventory/Effects";
import { Wheel } from "./Wheel";
import { Global } from "../utils/Global";
import { ItemType, SlotType } from "../inventory/BaseItem";
import { DropZoneShape, ItemDropZone } from "../utils/ItemDropZone";
import Client from "..";
import { NumberCounter } from "../gui/NumberCounter";


export enum WheelSectionColors {
  RED = 0xe82337,
  GREEN = 0x2cc94e,
  BLUE = 0x4c89ee,
  YELLOW = 0xfff57a,
  BLACK = 0x040519,
}

export class WheelSection extends Phaser.GameObjects.Container {
  private _score: number;
  public set score(value: number) {
    if (this.fieldText instanceof NumberCounter) {
      this.fieldText.OnNumberUpdateEvent(value);
    }

    this._score = value;
  }
  public get score(): number {
    return this._score;
  }

  public fieldSize: number;
  public text: string | null;
  public fieldColor: WheelSectionColors;
  public effects: Effect[];

  public pegs: number;
  public wheel: Wheel;

  public dropZone!: ItemDropZone<ItemType | SlotType>;

  private graphics: Phaser.GameObjects.Graphics;
  private fieldText: NumberCounter | Phaser.GameObjects.BitmapText | null =
    null;
  private debugIndexText: Phaser.GameObjects.BitmapText | null = null;

  private hoverTween?: Phaser.Tweens.Tween;
  private isHovered = false;
  private normalScale = 1.0;
  private hoverScale = 1.1;

  constructor(
    wheel: Wheel,
    scene: Phaser.Scene,
    score: number,
    fieldSize: number,
    fieldColor: WheelSectionColors,
    text: string | null = null,
    effects: Effect[] = [],
    pegs: number = 1, // Default: one peg
  ) {
    super(scene);
    this.wheel = wheel;
    this._score = score;
    this.fieldSize = fieldSize;
    this.fieldColor = fieldColor;
    this.text = text;
    this.effects = effects;
    this.pegs = pegs;

    // Add this container to the wheel immediately (as requested)
    this.wheel.add(this);

    this.graphics = new Phaser.GameObjects.Graphics(scene);
    this.add(this.graphics);

    if (text) {
      this.fieldText = new Phaser.GameObjects.BitmapText(
        this.scene,
        0,
        0,
        "fnt_paintBasic",
        text,
      );
      this.fieldText.setOrigin(0.5);
      this.add(this.fieldText);
    } else {
      this.fieldText = new NumberCounter(this.scene, score)
        .SetShowPositiveSign(true)
        .setOrigin(0.5)
        .SetAnimationScale(1.3, 0.7)
        .SetAnimationColor(0xffffff, 0xffffff);
      this.add(this.fieldText);
    }

    this.dropZone = new ItemDropZone(
      this.scene,
      [ItemType.MODIFIER],
      this.OnZoneEnter.bind(this),
      this.OnZoneLeave.bind(this),
      this.OnZoneDrop.bind(this),
    );
    this.add(this.dropZone);

    // Create debug text if debug mode is enabled
    if (Global.debug) {
      this.debugIndexText = new Phaser.GameObjects.BitmapText(
        this.scene,
        0,
        0,
        "fnt_paintBasic",
        "", // Will be set in draw() method
      );
      this.debugIndexText.setOrigin(0.5);
      this.debugIndexText.setTint(0xff00ff); // Magenta color for visibility
      this.add(this.debugIndexText);
    }
  }

  /**
   * Draw this slice. Starts at local -90° (12 o'clock) and spans `fieldSize` degrees.
   * The Wheel will rotate this container to its actual start angle.
   * @param radius Outer radius of the wheel.
   * @param fieldSizeOverride Optional new size (deg) if this field changed.
   */
  public draw(radius: number, fieldSizeOverride: number | null = null): void {
    if (fieldSizeOverride !== null) {
      this.fieldSize = fieldSizeOverride;
    }

    const start = Phaser.Math.DegToRad(-90);
    const end = Phaser.Math.DegToRad(-90 + this.fieldSize);

    this.graphics.clear();

    // Fill
    this.graphics.fillStyle(this.fieldColor, 1);
    this.graphics.beginPath();
    this.graphics.moveTo(0, 0);
    this.graphics.arc(0, 0, radius, start, end, false);
    this.graphics.closePath();
    this.graphics.fillPath();

    // Outline
    this.graphics.lineStyle(2, 0x000000, 1);
    this.graphics.beginPath();
    this.graphics.moveTo(0, 0);
    this.graphics.arc(0, 0, radius, start, end, false);
    this.graphics.closePath();
    this.graphics.strokePath();

    // Draw all pegs for this section
    this.drawPegs(radius, start, end);

    // Text
    if (this.fieldText) {
      const mid = (start + end) / 2;
      const rText = radius * 0.6; // radial placement for readability
      const tx = rText * Math.cos(mid);
      const ty = rText * Math.sin(mid);

      this.fieldText.setPosition(tx, ty);
      this.fieldText.setAngle(Phaser.Math.RadToDeg(mid));
      this.fieldText.setFontSize(Math.max(Global.minFontSize, radius * 0.12));
    }

    // Debug text showing section index
    if (Global.debug && this.debugIndexText) {
      const sectionIndex = this.wheel.sections.indexOf(this);
      this.debugIndexText.setText(`[${sectionIndex}]`);

      const mid = (start + end) / 2;
      const rDebugText = radius * 0.9; // Place debug text closer to the edge
      const debugTx = rDebugText * Math.cos(mid);
      const debugTy = rDebugText * Math.sin(mid);

      this.debugIndexText.setPosition(debugTx, debugTy);
      this.debugIndexText.setAngle(Phaser.Math.RadToDeg(mid));
      this.debugIndexText.setFontSize(
        Math.max(Global.minFontSize, radius * 0.12),
      );
    }

    // Setup interactive area after drawing
    this.setupInteractiveArea(radius);
  }

  public addPeg(): void {
    this.pegs++;
  }

  public removePeg(): void {
    if (this.pegs > 0) {
      this.pegs--;
    }
  }

  public setPegCount(count: number): void {
    this.pegs = Math.max(0, Math.floor(count));
  }

  public getPegCount(): number {
    return this.pegs;
  }

  public getPegAngles(fieldStartAngle: number): number[] {
    const fieldStartRad = Phaser.Math.DegToRad(fieldStartAngle);
    const fieldSizeRad = Phaser.Math.DegToRad(this.fieldSize);

    const pegPositions = this.calculatePegPositions();
    return pegPositions.map((pegRatio) => {
      return fieldStartRad + fieldSizeRad * pegRatio;
    });
  }

  public forceEndHover(): void {
    this.onHoverEnd(true); // Skip tween for instant reset
  }

  public Supports(itemType: ItemType): boolean {
    return itemType === ItemType.MODIFIER;
  }

  public OnZoneEnter(_effects?: Effect[]): void {
    this.onHoverStart();
  }

  public OnZoneLeave(_effects?: Effect[]): void {
    this.onHoverEnd();
  }

  public OnZoneDrop(effects?: Effect[]): boolean {
    effects?.forEach((effect) => {
      effect.OnUseEffect(this);
      if (effect.duration !== 0) this.effects.push(effect);
    });

    return true;
  }

  override destroy(fromScene?: boolean): void {
    // Clean up hover animations
    if (this.hoverTween) {
      this.hoverTween.destroy();
      this.hoverTween = undefined;
    }

    // Clean up debug text
    if (this.debugIndexText) {
      this.debugIndexText.destroy();
      this.debugIndexText = null;
    }

    // Remove event listeners
    this.off("pointerover", this.onHoverStart, this);
    this.off("pointerout", () => this.onHoverEnd(false), this);

    super.destroy(fromScene);
  }

  /**
   * Draw all pegs for this section on the outer edge
   */
  private drawPegs(radius: number, start: number, end: number): void {
    const pegRadius = radius * 0.02; // size of the peg
    const pegDistance = radius + pegRadius / 2; // place peg slightly outside the wheel edge

    // Calculate peg positions based on count
    const pegPositions = this.calculatePegPositions();

    for (const pegRatio of pegPositions) {
      // Calculate the angle for this peg based on its ratio within the arc
      const pegAngle = start + (end - start) * pegRatio;

      // Calculate peg position
      const pegX = pegDistance * Math.cos(pegAngle);
      const pegY = pegDistance * Math.sin(pegAngle);

      // Draw the peg as a small black circle
      this.graphics.fillStyle(0x040519, 1); // black color
      this.graphics.fillCircle(pegX, pegY, pegRadius);

      // Add a small white outline to make it more visible
      this.graphics.lineStyle(1, 0xffffff, 1);
      this.graphics.strokeCircle(pegX, pegY, pegRadius);
    }
  }

  /**
   * Calculate peg positions as ratios based on the number of pegs
   * 1. Remove padding from both sides
   * 2. Split remaining space into equal chunks
   * 3. Position each peg in the CENTER of its chunk
   */
  private calculatePegPositions(): number[] {
    if (this.pegs <= 0) return [];
    if (this.pegs === 1) return [0.5]; // Single peg in center

    const edgePadding = 0.03; // 3% padding from each edge
    const availableSpace = 1 - 2 * edgePadding; // Space between the padded edges
    const chunkSize = availableSpace / this.pegs; // Size of each chunk
    const positions: number[] = [];

    for (let i = 0; i < this.pegs; i++) {
      // Position peg in the center of its chunk
      const chunkCenter = (i + 0.5) * chunkSize; // Center of chunk i
      const pegPosition = edgePadding + chunkCenter;
      positions.push(pegPosition);
    }

    return positions;
  }

  /**
   * Setup interactive area based on the actual wheel section shape
   */
  private setupInteractiveArea(radius: number): void {
    // Create a polygon that matches the wheel section shape
    const start = Phaser.Math.DegToRad(-90);
    const end = Phaser.Math.DegToRad(-90 + this.fieldSize);

    // Create points for the sector shape
    const points: number[] = [];
    points.push(0, 0); // Center point

    // Add arc points
    const steps = Math.max(8, Math.floor(this.fieldSize / 10)); // More points for larger sections
    for (let i = 0; i <= steps; i++) {
      const angle = start + (end - start) * (i / steps);
      const x = radius * Math.cos(angle);
      const y = radius * Math.sin(angle);
      points.push(x, y);
    }

    /*
    // Draw debug visualization of hit area
    this.graphics.setDepth(9999); // Ensure it's drawn on top
    this.graphics.lineStyle(2, 0x00ffff, 1); // Bright purple outline
    this.graphics.beginPath();
    this.graphics.moveTo(points[0], points[1]);
    for (let i = 2; i < points.length; i += 2) {
      this.graphics.lineTo(points[i], points[i + 1]);
    }
    this.graphics.closePath();
    this.graphics.strokePath();
    */

    // If its already interactive just modify the area, otherwise create it
    if (this.input && this.input.hitArea) {
      this.input.hitArea.setTo(points);
    } else {
      this.setInteractive(
        new Phaser.Geom.Polygon(points),
        Phaser.Geom.Polygon.Contains,
      );

      // Setup hover events
      this.on("pointerover", this.onHoverStart, this);
      this.on("pointerout", () => this.onHoverEnd(false), this); // Explicitly pass false for smooth animation
    }

    this.dropZone.SetShape(DropZoneShape.POLYGON, points);
  }

  /**
   * Handle mouse hover start
   */
  private onHoverStart(): void {
    // Only allow hover effects when wheel is not spinning
    if (this.isHovered || Client.state.wheelSpinning) return;
    this.isHovered = true;

    // Cancel any existing tween
    if (this.hoverTween) {
      this.hoverTween.destroy();
      this.hoverTween = undefined;
    }

    // Animate to hover scale
    this.hoverTween = this.scene.tweens.add({
      targets: this,
      scaleX: this.hoverScale,
      scaleY: this.hoverScale,
      duration: 200,
      ease: "Power2",
      onComplete: () => {
        this.hoverTween = undefined;
      },
    });

    // Show descriptor window
    const pointer = this.scene.input.activePointer;
    this.wheel.showSectionDescriptor(this, pointer.x, pointer.y);
  }

  /**
   * Handle mouse hover end
   */
  private onHoverEnd(skipTween: boolean = false): void {
    if (!this.isHovered) return;
    this.isHovered = false;

    // Cancel any existing tween
    if (this.hoverTween) {
      this.hoverTween.destroy();
      this.hoverTween = undefined;
    }

    if (skipTween) {
      // Instantly set to normal scale
      this.setScale(this.normalScale, this.normalScale);
      return;
    }

    // Animate back to normal scale
    this.hoverTween = this.scene.tweens.add({
      targets: this,
      scaleX: this.normalScale,
      scaleY: this.normalScale,
      duration: 200,
      ease: "Power2",
      onComplete: () => {
        this.hoverTween = undefined;
      },
    });

    // Hide descriptor window
    this.wheel.hideSectionDescriptor();
  }
}
